import cn from 'classnames';
import { Spinner } from '#assets/Spinner.tsx';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { HelpText } from '#components/atoms/forms/index.ts';
import { QRCode } from '#components/atoms/qr/QRCode.tsx';
import type { EnrollmentState } from '../types.ts';
import { AppStoreButtons } from './AppStoreButtons.tsx';
import { mobileEnrollLink } from './mobile-enroll-link.ts';
import styles from './MobileInstructions.module.css';

type Props = {
  enrollmentState: EnrollmentState;
  iosURL?: string;
  androidURL?: string;
};

export function MobileInstructions({ enrollmentState, iosURL, androidURL }: Props) {
  const { token, enrollSuccessful, handleDone } = enrollmentState;

  if (!token) {
    return <p className="p-md mb-24">Getting enrollment code…</p>;
  }

  const mobileEnrollmentLink = mobileEnrollLink(token);

  return (
    <>
      <h2 className="h3">2. Install app</h2>
      <p className="p-md mb-16">Install Nebula Mobile on the device you’re enrolling.</p>
      <AppStoreButtons className="mb-40" iosURL={iosURL} androidURL={androidURL} />
      <h2 className="h3">3. Enroll mobile device</h2>

      <p className="p-md mb-24">
        Scan the QR code with the device you’re enrolling, or open the link to enroll the device you’re on. Codes expire
        in 24 hours.
      </p>
      <QRCode className="mb-24" value={mobileEnrollmentLink} title="QR code containing the enrollment link" small />

      <div className={cn('mb-24', styles.MobileInstructions_mobileEnrollmentContainer)}>
        {/* New tab on purpose: this is a cross-origin navigation to api.defined.net, and a same-tab
            hop unloads the os/arch state, the token poll, and the expiry timeout, losing the
            "detected host enrolled, redirecting" flow. Matches the wizard's copy of this link. */}
        <a
          className={cn('a p-md', styles.MobileInstructions_mobileEnrollmentLink)}
          href={mobileEnrollmentLink}
          target="_blank"
          rel="noreferrer"
        >
          {mobileEnrollmentLink}
        </a>
      </div>

      <HelpText className="mb-40">
        <Spinner className="mr-4" state={enrollSuccessful ? 'success' : 'spinning'} aria-hidden={true} size={12} />
        {enrollSuccessful ? 'Detected host enrolled, redirecting…' : 'Waiting for host…'}
      </HelpText>
      <Button variant="secondary" onClick={handleDone} disabled={enrollSuccessful}>
        Done
      </Button>
    </>
  );
}
